<script setup>
import { h, computed, onMounted } from 'vue';
import { provideSidebarContext } from './provider';
import { useAccount } from 'dashboard/composables/useAccount';
import { useKbd } from 'dashboard/composables/utils/useKbd';
import { useMapGetter } from 'dashboard/composables/store';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useStorage } from '@vueuse/core';
import { useSidebarKeyboardShortcuts } from './useSidebarKeyboardShortcuts';
import { vOnClickOutside } from '@vueuse/components';
import { useRoute } from 'vue-router';

import Button from 'dashboard/components-next/button/Button.vue';
import SidebarGroup from './SidebarGroup.vue';
import SidebarProfileMenu from './SidebarProfileMenu.vue';
import ChannelLeaf from './ChannelLeaf.vue';
import SidebarAccountSwitcher from './SidebarAccountSwitcher.vue';
import Logo from 'next/icon/Logo.vue';
import ComposeConversation from 'dashboard/components-next/NewConversation/ComposeConversation.vue';

const props = defineProps({
  isMobileSidebarOpen: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits([
  'closeKeyShortcutModal',
  'openKeyShortcutModal',
  'showCreateAccountModal',
  'closeMobileSidebar',
]);

const { accountScopedRoute } = useAccount();
const store = useStore();
const route = useRoute();
const searchShortcut = useKbd([`$mod`, 'k']);
const { t } = useI18n();

// Get account ID from route
const currentAccountId = computed(() => Number(route.params?.accountId));

// Helper to check if a specific feature is enabled
const isFeatureEnabled = (featureName) => {
  return store.getters['accounts/isFeatureEnabledonAccount'](currentAccountId.value, featureName);
};

// Check if tool_boletos feature is enabled for current account
const isToolBoletosEnabled = computed(() => isFeatureEnabled('tool_boletos'));

// Check if tool_wa_campaigns feature is enabled for current account
const isToolWaCampaignsEnabled = computed(() => isFeatureEnabled('tool_wa_campaigns'));

// Check if wa_catalog feature is enabled for current account
const isWaCatalogEnabled = computed(() => isFeatureEnabled('wa_catalog'));

// Check if payment_hub feature is enabled for current account
const isPaymentHubEnabled = computed(() => isFeatureEnabled('payment_hub'));

// Check if viper_printer feature is enabled for current account
const isViperPrinterEnabled = computed(() => isFeatureEnabled('viper_printer'));

// Check if calendar feature is enabled for current account
const isCalendarEnabled = computed(() => isFeatureEnabled('calendar'));

// Check if kanban_board feature is enabled for current account
const isKanbanEnabled = computed(() => isFeatureEnabled('kanban_board'));

// Check if agent_bots feature is enabled for current account
const isAgentBotsEnabled = computed(() => isFeatureEnabled('agent_bots'));

// Check if knowledge_base feature is enabled for current account
const isKnowledgeBaseEnabled = computed(() => isFeatureEnabled('knowledge_base'));

// Check if custom reports add-on is enabled for current account
const isCustomReportsEnabled = computed(() =>
  isFeatureEnabled('tool_custom_reports')
);

const toggleShortcutModalFn = show => {
  if (show) {
    emit('openKeyShortcutModal');
  } else {
    emit('closeKeyShortcutModal');
  }
};

useSidebarKeyboardShortcuts(toggleShortcutModalFn);

// We're using localStorage to store the expanded item in the sidebar
// This helps preserve context when navigating between portal and dashboard layouts
// and also when the user refreshes the page
const expandedItem = useStorage(
  'next-sidebar-expanded-item',
  null,
  sessionStorage
);

const setExpandedItem = name => {
  expandedItem.value = expandedItem.value === name ? null : name;
};
provideSidebarContext({
  expandedItem,
  setExpandedItem,
});

const inboxes = useMapGetter('inboxes/getInboxes');
const labels = useMapGetter('labels/getLabelsOnSidebar');
const teams = useMapGetter('teams/getMyTeams');
const contactCustomViews = useMapGetter('customViews/getContactCustomViews');
const conversationCustomViews = useMapGetter(
  'customViews/getConversationCustomViews'
);
const settingsPermissions = useMapGetter('getCurrentSettingsPermissions');
const currentRole = useMapGetter('getCurrentRole');

// Check if user has a specific settings permission
const hasSettingsPermission = permission => {
  if (currentRole.value === 'administrator') return true;
  // If settings_permissions is empty/null, agent has NO access to settings
  if (!settingsPermissions.value || settingsPermissions.value.length === 0) return false;
  return settingsPermissions.value.includes(permission);
};

onMounted(() => {
  store.dispatch('labels/get');
  store.dispatch('inboxes/get');
  store.dispatch('notifications/unReadCount');
  store.dispatch('teams/get');
  store.dispatch('attributes/get');
  store.dispatch('customViews/get', 'conversation');
  store.dispatch('customViews/get', 'contact');
  store.dispatch('pendingQuestionsCount/fetchCount');
});

const sortedInboxes = computed(() =>
  inboxes.value.slice().sort((a, b) => a.name.localeCompare(b.name))
);

const closeMobileSidebar = () => {
  if (!props.isMobileSidebarOpen) return;
  emit('closeMobileSidebar');
};

const newReportRoutes = () => [
  {
    name: 'Reports Agent',
    label: t('SIDEBAR.REPORTS_AGENT'),
    to: accountScopedRoute('agent_reports_index'),
    activeOn: ['agent_reports_show'],
  },
  {
    name: 'Reports Label',
    label: t('SIDEBAR.REPORTS_LABEL'),
    to: accountScopedRoute('label_reports_index'),
  },
  {
    name: 'Reports Inbox',
    label: t('SIDEBAR.REPORTS_INBOX'),
    to: accountScopedRoute('inbox_reports_index'),
    activeOn: ['inbox_reports_show'],
  },
  {
    name: 'Reports Team',
    label: t('SIDEBAR.REPORTS_TEAM'),
    to: accountScopedRoute('team_reports_index'),
    activeOn: ['team_reports_show'],
  },
];

const reportRoutes = computed(() => newReportRoutes());

const menuItems = computed(() => {
  return [
    {
      name: 'Conversation',
      label: t('SIDEBAR.CONVERSATIONS'),
      icon: 'i-lucide-message-circle',
      children: [
        // Kanban - conditionally shown based on feature flag
        ...(isKanbanEnabled.value ? [{
          name: 'Kanban',
          label: t('SIDEBAR.KANBAN'),
          to: accountScopedRoute('kanban_index'),
          activeOn: ['kanban_index'],
        }] : []),
        {
          name: 'All',
          label: t('SIDEBAR.ALL_CONVERSATIONS'),
          activeOn: ['inbox_conversation', 'home'],
          to: accountScopedRoute('home'),
        },
        {
          name: 'Folders',
          label: t('SIDEBAR.CUSTOM_VIEWS_FOLDER'),
          icon: 'i-lucide-folder',
          activeOn: ['conversations_through_folders'],
          children: conversationCustomViews.value.map(view => ({
            name: `${view.name}-${view.id}`,
            label: view.name,
            to: accountScopedRoute('folder_conversations', { id: view.id }),
          })),
        },
        {
          name: 'Teams',
          label: t('SIDEBAR.TEAMS'),
          icon: 'i-lucide-users',
          activeOn: ['conversations_through_team'],
          children: teams.value.map(team => ({
            name: `${team.name}-${team.id}`,
            label: team.name,
            to: accountScopedRoute('team_conversations', { teamId: team.id }),
          })),
        },
        {
          name: 'Labels',
          label: t('SIDEBAR.LABELS'),
          icon: 'i-lucide-tag',
          activeOn: ['conversations_through_label'],
          children: labels.value.map(label => ({
            name: `${label.title}-${label.id}`,
            label: label.title,
            icon: h('span', {
              class: `size-[12px] ring-1 ring-n-alpha-1 dark:ring-white/20 ring-inset rounded-sm`,
              style: { backgroundColor: label.color },
            }),
            to: accountScopedRoute('label_conversations', {
              label: label.title,
            }),
          })),
        },
      ],
    },
    // Calendar item - conditionally shown based on feature flag
    ...(isCalendarEnabled.value ? [{
      name: 'Calendar',
      label: t('SIDEBAR.CALENDAR'),
      icon: 'i-lucide-calendar',
      to: accountScopedRoute('calendar_index'),
      activeOn: ['calendar_index', 'calendar_settings', 'calendar_types'],
    }] : []),
    {
      name: 'Contacts',
      label: t('SIDEBAR.CONTACTS'),
      icon: 'i-lucide-contact',
      children: [
        {
          name: 'All Contacts',
          label: t('SIDEBAR.ALL_CONTACTS'),
          to: accountScopedRoute(
            'contacts_dashboard_index',
            {},
            { page: 1, search: undefined }
          ),
          activeOn: ['contacts_dashboard_index', 'contacts_edit'],
        },
        {
          name: 'Active',
          label: t('SIDEBAR.ACTIVE'),
          to: accountScopedRoute('contacts_dashboard_active'),
          activeOn: ['contacts_dashboard_active'],
        },
        {
          name: 'Segments',
          icon: 'i-lucide-group',
          label: t('SIDEBAR.CUSTOM_VIEWS_SEGMENTS'),
          children: contactCustomViews.value.map(view => ({
            name: `${view.name}-${view.id}`,
            label: view.name,
            to: accountScopedRoute(
              'contacts_dashboard_segments_index',
              { segmentId: view.id },
              { page: 1 }
            ),
            activeOn: [
              'contacts_dashboard_segments_index',
              'contacts_edit_segment',
            ],
          })),
        },
        {
          name: 'Tagged With',
          icon: 'i-lucide-tag',
          label: t('SIDEBAR.TAGGED_WITH'),
          children: labels.value.map(label => ({
            name: `${label.title}-${label.id}`,
            label: label.title,
            icon: h('span', {
              class: `size-[12px] ring-1 ring-n-alpha-1 dark:ring-white/20 ring-inset rounded-sm`,
              style: { backgroundColor: label.color },
            }),
            to: accountScopedRoute(
              'contacts_dashboard_labels_index',
              { label: label.title },
              { page: 1, search: undefined }
            ),
            activeOn: [
              'contacts_dashboard_labels_index',
              'contacts_edit_label',
            ],
          })),
        },
      ],
    },
    // Tools section - each tool is controlled by its own feature flag
    // Only show the section if at least one tool is enabled and user has permission
    ...(() => {
      const toolsChildren = [
        // Each tool checks its own feature flag
        ...(isToolBoletosEnabled.value ? [{
          name: 'Tools Boletos',
          label: t('SIDEBAR.TOOLS_BOLETOS'),
          icon: 'i-lucide-file-text',
          to: accountScopedRoute('omni_boletos'),
          settingsPermission: 'boletos_manage',
        }] : []),
        ...(isToolWaCampaignsEnabled.value ? [{
          name: 'Tools WaCampaigns',
          label: t('SIDEBAR.TOOLS_WA_CAMPAIGNS'),
          icon: 'i-lucide-megaphone',
          to: accountScopedRoute('omni_wa_campaigns'),
          settingsPermission: 'wa_campaigns_manage',
        }] : []),
        ...(isWaCatalogEnabled.value ? [{
          name: 'Tools WaCatalog',
          label: t('SIDEBAR.TOOLS_WA_CATALOG'),
          icon: 'i-lucide-shopping-bag',
          to: accountScopedRoute('wa_catalog_index'),
        }] : []),
        ...(isPaymentHubEnabled.value ? [{
          name: 'Tools PaymentHub',
          label: t('SIDEBAR.TOOLS_PAYMENT_HUB'),
          icon: 'i-lucide-wallet',
          to: accountScopedRoute('payment_hub_index'),
        }] : []),
        ...(isViperPrinterEnabled.value ? [{
          name: 'Tools ViperPrinter',
          label: t('SIDEBAR.TOOLS_VIPER_PRINTER'),
          icon: 'i-lucide-printer',
          to: accountScopedRoute('viper_printer_orders'),
        }] : []),
      ].filter(item => {
        // If has settingsPermission, check if user has that permission
        if (item.settingsPermission) {
          return hasSettingsPermission(item.settingsPermission);
        }
        return true;
      });

      // Only return the Tools section if there are visible children
      return toolsChildren.length > 0 ? [{
        name: 'Tools',
        label: t('SIDEBAR.TOOLS'),
        icon: 'i-lucide-wrench',
        children: toolsChildren,
      }] : [];
    })(),
    {
      name: 'Reports',
      label: t('SIDEBAR.REPORTS'),
      icon: 'i-lucide-chart-spline',
      children: [
        {
          name: 'Report Overview',
          label: t('SIDEBAR.REPORTS_OVERVIEW'),
          to: accountScopedRoute('account_overview_reports'),
        },
        {
          name: 'Report Conversation',
          label: t('SIDEBAR.REPORTS_CONVERSATION'),
          to: accountScopedRoute('conversation_reports'),
        },
        ...reportRoutes.value,
        {
          name: 'Reports CSAT',
          label: t('SIDEBAR.CSAT'),
          to: accountScopedRoute('csat_reports'),
        },
        {
          name: 'Reports SLA',
          label: t('SIDEBAR.REPORTS_SLA'),
          to: accountScopedRoute('sla_reports'),
        },
        {
          name: 'Reports Bot',
          label: t('SIDEBAR.REPORTS_BOT'),
          to: accountScopedRoute('bot_reports'),
        },
        ...(isCustomReportsEnabled.value
          ? [
              {
                name: 'Reports Custom',
                label: t('SIDEBAR.REPORTS_CUSTOM'),
                to: accountScopedRoute('custom_reports'),
              },
            ]
          : []),
      ],
    },
    {
      name: 'Campaigns',
      label: t('SIDEBAR.CAMPAIGNS'),
      icon: 'i-lucide-megaphone',
      children: [
        {
          name: 'Live chat',
          label: t('SIDEBAR.LIVE_CHAT'),
          to: accountScopedRoute('campaigns_livechat_index'),
        },
        {
          name: 'SMS',
          label: t('SIDEBAR.SMS'),
          to: accountScopedRoute('campaigns_sms_index'),
        },
        {
          name: 'WhatsApp',
          label: t('SIDEBAR.WHATSAPP'),
          to: accountScopedRoute('campaigns_whatsapp_index'),
        },
      ],
    },
    {
      name: 'Portals',
      label: t('SIDEBAR.HELP_CENTER.TITLE'),
      icon: 'i-lucide-library-big',
      children: [
        ...(isKnowledgeBaseEnabled.value ? [{
          name: 'KnowledgeBase',
          label: t('SIDEBAR.HELP_CENTER.KNOWLEDGE_BASE'),
          activeOn: ['knowledge_base_index', 'knowledge_base_article'],
          to: accountScopedRoute('knowledge_base_index'),
        }] : []),
        {
          name: 'Articles',
          label: t('SIDEBAR.HELP_CENTER.MY_ARTICLES'),
          activeOn: [
            'portals_articles_index',
            'portals_articles_new',
            'portals_articles_edit',
            'portals_categories_index',
            'portals_categories_articles_index',
            'portals_categories_articles_edit',
            'portals_locales_index',
            'portals_settings_index',
          ],
          to: accountScopedRoute('portals_index', {
            navigationPath: 'portals_articles_index',
          }),
          settingsPermission: 'help_center_manage',
        },
        {
          name: 'SupportConversation',
          label: t('SIDEBAR.HELP_CENTER.CONTACT_US'),
          activeOn: ['support_conversation'],
          to: accountScopedRoute('support_conversation'),
        },
      ].filter(item => {
        if (item.settingsPermission) {
          return hasSettingsPermission(item.settingsPermission);
        }
        return true;
      }),
    },
    {
      name: 'Settings',
      label: t('SIDEBAR.SETTINGS'),
      icon: 'i-lucide-bolt',
      getterKeys: {
        count: 'pendingQuestionsCount/getCount',
      },
      children: [
        // Admin-only items (no settingsPermission needed - always visible to admins)
        {
          name: 'Settings Agents',
          label: t('SIDEBAR.AGENTS'),
          icon: 'i-lucide-square-user',
          to: accountScopedRoute('agent_list'),
          adminOnly: true,
        },
        {
          name: 'Settings Teams',
          label: t('SIDEBAR.TEAMS'),
          icon: 'i-lucide-users',
          to: accountScopedRoute('settings_teams_list'),
          adminOnly: true,
        },
        {
          name: 'Settings Agent Assignment',
          label: t('SIDEBAR.AGENT_ASSIGNMENT'),
          icon: 'i-lucide-user-cog',
          to: accountScopedRoute('assignment_policy_index'),
          adminOnly: true,
        },
        {
          name: 'Settings Inboxes',
          label: t('SIDEBAR.INBOXES'),
          icon: 'i-lucide-inbox',
          to: accountScopedRoute('settings_inbox_list'),
          adminOnly: true,
        },
        // Permission-based items
        {
          name: 'Settings Labels',
          label: t('SIDEBAR.LABELS'),
          icon: 'i-lucide-tags',
          to: accountScopedRoute('labels_list'),
          settingsPermission: 'labels_manage',
        },
        {
          name: 'Settings Custom Attributes',
          label: t('SIDEBAR.CUSTOM_ATTRIBUTES'),
          icon: 'i-lucide-code',
          to: accountScopedRoute('attributes_list'),
          settingsPermission: 'attributes_manage',
        },
        {
          name: 'Settings Automation',
          label: t('SIDEBAR.AUTOMATION'),
          icon: 'i-lucide-workflow',
          to: accountScopedRoute('automation_list'),
          settingsPermission: 'automation_manage',
        },
        // Kanban settings - conditionally shown based on feature flag
        ...(isKanbanEnabled.value ? [{
          name: 'Settings Kanban',
          label: t('SIDEBAR.KANBAN_SETTINGS'),
          icon: 'i-lucide-kanban',
          to: accountScopedRoute('kanban_settings'),
          settingsPermission: 'kanban_manage',
        }] : []),
        // Viper Printer settings - conditionally shown based on feature flag
        ...(isViperPrinterEnabled.value ? [{
          name: 'Settings Viper Printer',
          label: t('SIDEBAR.PRINTER_SETTINGS'),
          icon: 'i-lucide-printer',
          to: accountScopedRoute('viper_printer_settings'),
          adminOnly: true,
        }] : []),
        // Calendar settings - conditionally shown based on feature flag
        ...(isCalendarEnabled.value ? [{
          name: 'Settings Calendar',
          label: t('SIDEBAR.CALENDAR_SETTINGS'),
          icon: 'i-lucide-calendar-cog',
          to: accountScopedRoute('calendar_settings'),
          settingsPermission: 'calendar_manage',
        }] : []),
        {
          name: 'Settings Help Center',
          label: t('SIDEBAR.HELP_CENTER_SETTINGS'),
          icon: 'i-lucide-library-big',
          to: accountScopedRoute('portals_index', {
            navigationPath: 'portals_categories_index',
          }),
          settingsPermission: 'help_center_manage',
        },
        // Agent Bots related items - only shown when agent_bots feature is enabled
        ...(isAgentBotsEnabled.value ? [{
          name: 'Settings Stages',
          label: t('SIDEBAR.STAGES'),
          icon: 'i-lucide-box',
          to: accountScopedRoute('omni_slots_config'),
          settingsPermission: 'stages_manage',
        }] : []),
        ...(isAgentBotsEnabled.value ? [{
          name: 'Settings Knowledge Base',
          label: t('SIDEBAR.KNOWLEDGE_BASE'),
          icon: 'i-lucide-book-open',
          to: accountScopedRoute('omni_knowledge_base'),
          settingsPermission: 'response_templates_manage',
        }] : []),
        ...(isAgentBotsEnabled.value ? [{
          name: 'Settings Omni Pending Questions',
          label: t('SIDEBAR.OMNI_PENDING_QUESTIONS'),
          icon: 'i-lucide-message-circle-question',
          to: accountScopedRoute('omni_pending_questions'),
          settingsPermission: 'pending_questions_manage',
          getterKeys: {
            count: 'pendingQuestionsCount/getCount',
          },
        }] : []),
        ...(isAgentBotsEnabled.value ? [{
          name: 'Settings Omni Data Sources',
          label: t('SIDEBAR.OMNI_DATA_SOURCES'),
          icon: 'i-lucide-database',
          to: accountScopedRoute('omni_data_sources'),
          settingsPermission: 'data_sources_manage',
        }] : []),
        {
          name: 'Settings Omni Billing',
          label: t('SIDEBAR.OMNI_BILLING'),
          icon: 'i-lucide-credit-card',
          to: accountScopedRoute('omni_billing'),
          adminOnly: true,
        },
        {
          name: 'Settings Macros',
          label: t('SIDEBAR.MACROS'),
          icon: 'i-lucide-toy-brick',
          to: accountScopedRoute('macros_wrapper'),
          settingsPermission: 'macros_manage',
        },
        {
          name: 'Settings Canned Responses',
          label: t('SIDEBAR.CANNED_RESPONSES'),
          icon: 'i-lucide-message-square-quote',
          to: accountScopedRoute('canned_list'),
          settingsPermission: 'canned_responses_manage',
        },
        {
          name: 'Settings Integrations',
          label: t('SIDEBAR.INTEGRATIONS'),
          icon: 'i-lucide-blocks',
          to: accountScopedRoute('settings_applications'),
          adminOnly: true,
        },
        {
          name: 'Settings Audit Logs',
          label: t('SIDEBAR.AUDIT_LOGS'),
          icon: 'i-lucide-briefcase',
          to: accountScopedRoute('auditlogs_list'),
          adminOnly: true,
        },
        {
          name: 'Settings Sla',
          label: t('SIDEBAR.SLA'),
          icon: 'i-lucide-clock-alert',
          to: accountScopedRoute('sla_list'),
          adminOnly: true,
        },
        {
          name: 'Settings Security',
          label: t('SIDEBAR.SECURITY'),
          icon: 'i-lucide-shield',
          to: accountScopedRoute('security_settings_index'),
          adminOnly: true,
        },
        {
          name: 'Settings Billing',
          label: t('SIDEBAR.BILLING'),
          icon: 'i-lucide-credit-card',
          to: accountScopedRoute('billing_settings_index'),
          adminOnly: true,
        },
      ].filter(item => {
        // If adminOnly, only show to administrators
        if (item.adminOnly) {
          return currentRole.value === 'administrator';
        }
        // If has settingsPermission, check if user has that permission
        if (item.settingsPermission) {
          return hasSettingsPermission(item.settingsPermission);
        }
        // Default: show to everyone
        return true;
      }),
    },
  ];
});
</script>

<template>
  <aside
    v-on-click-outside="[
      closeMobileSidebar,
      { ignore: ['#mobile-sidebar-launcher'] },
    ]"
    class="bg-n-solid-2 rtl:border-l ltr:border-r border-n-weak flex flex-col text-sm pb-1 fixed top-0 ltr:left-0 rtl:right-0 h-full z-40 transition-transform duration-200 ease-in-out md:static w-[200px] basis-[200px] md:flex-shrink-0 md:ltr:translate-x-0 md:rtl:-translate-x-0"
    :class="[
      {
        'shadow-lg md:shadow-none': isMobileSidebarOpen,
        'ltr:-translate-x-full rtl:translate-x-full': !isMobileSidebarOpen,
      },
    ]"
  >
    <section class="grid gap-2 mt-2 mb-4">
      <div class="flex items-center min-w-0 gap-2 px-2">
        <div class="grid flex-shrink-0 size-6 place-content-center">
          <Logo class="size-4" />
        </div>
        <div class="flex-shrink-0 w-px h-3 bg-n-strong" />
        <SidebarAccountSwitcher
          class="flex-grow min-w-0 -mx-1"
          @show-create-account-modal="emit('showCreateAccountModal')"
        />
      </div>
      <div class="flex gap-2 px-2">
        <RouterLink
          :to="{ name: 'search' }"
          class="flex items-center w-full gap-2 px-2 py-1 rounded-lg h-7 outline outline-1 outline-n-weak bg-n-solid-3 dark:bg-n-black/30"
        >
          <span class="flex-shrink-0 i-lucide-search size-4 text-n-slate-11" />
          <span class="flex-grow text-left">
            {{ t('COMBOBOX.SEARCH_PLACEHOLDER') }}
          </span>
          <span
            class="hidden tracking-wide pointer-events-none select-none text-n-slate-10"
          >
            {{ searchShortcut }}
          </span>
        </RouterLink>
        <ComposeConversation align-position="right">
          <template #trigger="{ toggle }">
            <Button
              icon="i-lucide-pen-line"
              color="slate"
              size="sm"
              class="!h-7 !bg-n-solid-3 dark:!bg-n-black/30 !outline-n-weak !text-n-slate-11"
              @click="toggle"
            />
          </template>
        </ComposeConversation>
      </div>
    </section>
    <nav class="grid flex-grow gap-2 px-2 pb-5 overflow-y-scroll no-scrollbar">
      <ul class="flex flex-col gap-1.5 m-0 list-none">
        <SidebarGroup
          v-for="item in menuItems"
          :key="item.name"
          v-bind="item"
        />
      </ul>
    </nav>
    <section
      class="p-1 border-t border-n-weak shadow-[0px_-2px_4px_0px_rgba(27,28,29,0.02)] flex-shrink-0 flex justify-between gap-2 items-center"
    >
      <SidebarProfileMenu
        @open-key-shortcut-modal="emit('openKeyShortcutModal')"
      />
    </section>
  </aside>
</template>
